// src/pages/packingBatch/PrintFormats/Label102x76Format4.jsx
import React, { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import styles from './Label102x76Format4.module.css';
import { getImageUrl, handleImageError } from '../../../utils/imageUtils';

const MAX_ITEMS = 7;

const Label102x76Format4 = ({ printData }) => {
  const barcodeRefs = useRef({});

  useEffect(() => {
    // Generate barcodes for all airwaybill numbers
    printData.orders.forEach((order) => {
      const barcodeElement = barcodeRefs.current[order.airwaybillNumber];
      if (barcodeElement && order.airwaybillNumber) {
        try {
          JsBarcode(barcodeElement, order.airwaybillNumber, {
            format: 'CODE128',
            width: 2,
            height: 46,
            displayValue: true,
            fontSize: 15,
            margin: 1,
            background: '#ffffff',
            lineColor: '#000000',
            fontOptions: 'bold'
          });
        } catch (error) {
          console.error('Failed to generate barcode:', error);
        }
      }
    });
  }, [printData]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPhone = (phones) => {
    if (!phones || phones.length === 0) return 'N/A';
    return phones.slice(0, 2).join(', ');
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className={styles.labelsContainer}>
      {printData.orders.map((order, index) => (
        <div key={order.orderId} className={styles.label}>
          {/* Top Section: Shop & Courier Info */}
          <div className={styles.topSection}>
            <div className={styles.leftColumn}>
              <div className={styles.shopBox}>
                {order.shop.logo ? (
                  <img 
                    src={getImageUrl(order.shop.logo)} 
                    alt={order.shop.name}
                    className={styles.shopLogo}
                    onError={(e) => handleImageError(e)}
                  />
                ) : (
                  <div className={styles.shopLogoPlaceholder}>
                    {order.shop.name.charAt(0)}
                  </div>
                )}
                <div className={styles.shopInfo}>
                  <div className={styles.shopName}>{truncateText(order.shop.name, 20)}</div>
                  {order.shop.phone && (
                    <div className={styles.shopPhone}>{order.shop.phone}</div>
                  )}
                </div>
              </div>
              
              <div className={styles.orderMeta}>
                <div className={styles.orderNumberGroup}>
                  <div className={styles.orderNumber}>#{order.orderNumber}</div>
                  <div className={styles.orderDate}>{formatDate(order.orderDate)}</div>
                </div>
                <div className={styles.badgeGroup}>
                  {order.isUrgent && (
                    <div className={styles.urgentBadge}>URGENT</div>
                  )}
                  {order.isReplacement && (
                    <div className={styles.exchangeBadge}>EXCHANGE</div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.rightColumn}>
              <div className={styles.barcodeContainer}>
                <svg
                  ref={(el) => (barcodeRefs.current[order.airwaybillNumber] = el)}
                  className={styles.barcode}
                />
              </div>
              <div className={styles.courierName}>
                {printData.batch?.account?.name || printData.batch?.courier?.name || order?.courierAccount?.name || 'N/A'}
              </div>
              {order.returnAirwaybillNumber && (
                <div className={styles.returnAwb}>
                  RETURN AWB: {order.returnAirwaybillNumber}
                </div>
              )}
            </div>
          </div>

          {/* Middle Section: Customer & Address */}
          <div className={styles.middleSection}>
            <div className={styles.customerBlock}>
              <div className={styles.blockLabel}>CUSTOMER</div>
              <div className={styles.customerName}>{order.customer.name}</div>
              <div className={styles.customerPhone}>
                📞 {formatPhone(order.customer.allPhones)}
              </div>
            </div>

            <div className={styles.addressBlock}>
              <div className={styles.blockLabel}>DELIVERY ADDRESS</div>
              <div className={styles.address}>{truncateText(order.customer.address, 75)}</div>
              <div className={styles.location}>
                📍 {order.customer.city}
                {order.customer.district && ` (${order.customer.district})`}
              </div>
            </div>
          </div>

          {/* Bottom Section: Items, Notes & COD */}
          <div className={styles.bottomSection}>
            <div className={styles.detailsColumn}>
              <div className={`${styles.itemsBlock} ${order.items.length > 6 ? styles.itemsDense : ''}`}>
                <div className={styles.blockLabel}>ITEMS ({order.itemsCount})</div>
                <div className={styles.itemsList}>
                  {order.items.slice(0, MAX_ITEMS).map((item, idx) => (
                    <div key={idx} className={styles.item}>
                      • {item.itemDisplayStr}
                    </div>
                  ))}
                  {order.items.length > MAX_ITEMS && (
                    <div className={styles.moreItems}>
                      +{order.items.length - MAX_ITEMS} more item(s)
                    </div>
                  )}
                </div>
              </div>

              {order.deliveryNotes && order.items.length <= 4 && (
                <div className={styles.notesBlock}>
                  <div className={styles.blockLabel}>NOTES</div>
                  <div className={styles.notes}>
                    {truncateText(order.deliveryNotes, 50)}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.codColumn}>
              <div className={styles.codBox}>
                <div className={styles.codLabel}>COD AMOUNT</div>
                <div className={styles.codAmount}>
                  Rs. {order.financials.codAmount.toLocaleString()}
                </div>
              </div>
              <div className={styles.warehouse}>
                {printData.batch?.warehouse?.name || order?.warehouse?.name || ''}
              </div>
              {order.deliveryNotes && order.items.length > 4 && (
                <div className={styles.notesCompact}>
                  <span className={styles.blockLabel}>NOTES: </span>
                  {truncateText(order.deliveryNotes, 40)}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Label102x76Format4;